import { cva } from '@/styled-system/css';

export const iconButtonRecipe = cva({
  base: {
    'alignItems': 'center',
    'cursor': 'pointer',
    'display': 'inline-flex',
    'justifyContent': 'center',
    'position': 'relative',
    "&[aria-pressed='true']:disabled": {
      opacity: 1,
    },
    'boxShadowColor': 'transparent.invisible', // Sets a default `var(--shadow-color)` so it is always defined
    'transition': {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow 200ms ease, background-color 200ms ease',
    },
  },
  variants: {
    tone: {
      'brand': {},
      'neutral': {},
      'neutral-fixed': {},
      'critical': {},
    },
    emphasis: {
      bare: {
        focusOutline: true,
      },
      filled: {
        boxShadow: {
          base: `inset 0 0 0 {borderWidths.IconButton.default} var(--shadow-color)`,
          _focusVisible: `inset 0 0 0 {borderWidths.IconButton.default} var(--shadow-color), {shadows.focus}`,
        },
      },
      outlined: {
        boxShadow: {
          base: `inset 0 0 0 {borderWidths.IconButton.default} var(--shadow-color)`,
          _focusVisible: `inset 0 0 0 {borderWidths.IconButton.default} var(--shadow-color), {shadows.focus}`,
        },
      },
    },
    disabled: {
      true: {
        opacity: 'disabled',
        cursor: 'default',
      },
      false: {
        cursor: 'pointer',
      },
    },
    onImpact: {
      true: {},
      false: {},
    },
    shape: {
      circle: {
        borderRadius: 'IconButton.circle',
      },
      square: {
        borderRadius: 'IconButton.square',
      },
    },
    size: {
      xs: {
        'height': 'IconButton.xs',
        'width': 'IconButton.xs',
        '& > :where(svg)': {
          width: 'icon.sm',
          height: 'icon.sm',
        },
      },
      sm: {
        'height': 'IconButton.sm',
        'width': 'IconButton.sm',
        '& > :where(svg)': {
          width: 'icon.sm',
          height: 'icon.sm',
        },
      },
      md: {
        'height': 'IconButton.md',
        'width': 'IconButton.md',
        '& > :where(svg)': {
          width: 'icon.md',
          height: 'icon.md',
        },
      },
      lg: {
        'height': 'IconButton.lg',
        'width': 'IconButton.lg',
        '& > :where(svg)': {
          width: 'icon.md',
          height: 'icon.md',
        },
      },
    },
  },
  compoundVariants: [
    {
      size: ['xs', 'sm'],
      css: {
        _after: {
          content: '""',
          position: 'absolute',
          height: 'tapArea.mobile.default',
          minWidth: 'tapArea.mobile.default',
          width: '100%',
          left: '50%',
          top: '50%',
          marginBlockStart: 'calc(({sizes.tapArea.mobile.default} / 2) * -1)',
          marginInlineStart: 'calc(({sizes.tapArea.mobile.default} / 2) * -1)',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'IconButton.filled.brand.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.filled.brand.border.default',
        },
        color: {
          base: 'IconButton.filled.brand.icon.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'IconButton.filled.critical.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.filled.critical.border.default',
        },
        color: {
          base: 'IconButton.filled.critical.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'IconButton.outlined.brand.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.outlined.brand.border.default',
        },
        color: {
          base: 'IconButton.outlined.brand.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'IconButton.outlined.neutral.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.outlined.neutral.border.default',
        },
        color: {
          base: 'IconButton.outlined.neutral.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'IconButton.outlined.critical.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.outlined.critical.border.default',
        },
        color: {
          base: 'IconButton.outlined.critical.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'IconButton.outlined.neutral-fixed.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.outlined.neutral-fixed.border.default',
        },
        color: {
          base: 'IconButton.outlined.neutral-fixed.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'IconButton.bare.brand.bg.default',
        },
        color: {
          base: 'IconButton.bare.brand.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'IconButton.bare.neutral.bg.default',
        },
        color: {
          base: 'IconButton.bare.neutral.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'IconButton.bare.critical.bg.default',
        },
        color: {
          base: 'IconButton.bare.critical.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'IconButton.bare.neutral-fixed.bg.default',
        },
        color: {
          base: 'IconButton.bare.neutral-fixed.icon.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.filled.brand.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.filled.brand.border.default',
        },
        color: {
          base: 'IconButton.onImpact.filled.brand.icon.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.filled.critical.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.filled.critical.border.default',
        },
        color: {
          base: 'IconButton.onImpact.filled.critical.icon.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'neutral-fixed',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.filled.neutral-fixed.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.filled.neutral-fixed.border.default',
        },
        color: {
          base: 'IconButton.onImpact.filled.neutral-fixed.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.outlined.brand.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.outlined.brand.border.default',
        },
        color: {
          base: 'IconButton.onImpact.outlined.brand.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.outlined.neutral.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.outlined.neutral.border.default',
        },
        color: {
          base: 'IconButton.onImpact.outlined.neutral.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.outlined.critical.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.outlined.critical.border.default',
        },
        color: {
          base: 'IconButton.onImpact.outlined.critical.icon.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.outlined.neutral-fixed.bg.default',
        },
        boxShadowColor: {
          base: 'IconButton.onImpact.outlined.neutral-fixed.border.default',
        },
        color: {
          base: 'IconButton.onImpact.outlined.neutral-fixed.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.bare.neutral.bg.default',
        },
        color: {
          base: 'IconButton.onImpact.bare.neutral.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.bare.neutral-fixed.bg.default',
        },
        color: {
          base: 'IconButton.onImpact.bare.neutral-fixed.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.bare.brand.bg.default',
        },
        color: {
          base: 'IconButton.onImpact.bare.brand.icon.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      onImpact: true,
      css: {
        backgroundColor: {
          base: 'IconButton.onImpact.bare.critical.bg.default',
        },
        color: {
          base: 'IconButton.onImpact.bare.critical.icon.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.filled.brand.bg.hover',
          _active: 'IconButton.filled.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.filled.brand.border.hover',
          _active: 'IconButton.filled.brand.border.pressed',
        },
        color: {
          _hover: 'IconButton.filled.brand.icon.hover',
          _active: 'IconButton.filled.brand.icon.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.filled.critical.bg.hover',
          _active: 'IconButton.filled.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.filled.critical.border.hover',
          _active: 'IconButton.filled.critical.border.pressed',
        },
        color: {
          _hover: 'IconButton.filled.critical.icon.hover',
          _active: 'IconButton.filled.critical.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.outlined.brand.bg.hover',
          _active: 'IconButton.outlined.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.outlined.brand.border.hover',
          _active: 'IconButton.outlined.brand.border.pressed',
        },
        color: {
          _hover: 'IconButton.outlined.brand.icon.hover',
          _active: 'IconButton.outlined.brand.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.outlined.neutral.bg.hover',
          _active: 'IconButton.outlined.neutral.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.outlined.neutral.border.hover',
          _active: 'IconButton.outlined.neutral.border.pressed',
        },
        color: {
          _hover: 'IconButton.outlined.neutral.icon.hover',
          _active: 'IconButton.outlined.neutral.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.outlined.critical.bg.hover',
          _active: 'IconButton.outlined.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.outlined.critical.border.hover',
          _active: 'IconButton.outlined.critical.border.pressed',
        },
        color: {
          _hover: 'IconButton.outlined.critical.icon.hover',
          _active: 'IconButton.outlined.critical.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.outlined.neutral-fixed.bg.hover',
          _active: 'IconButton.outlined.neutral-fixed.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.outlined.neutral-fixed.border.hover',
          _active: 'IconButton.outlined.neutral-fixed.border.pressed',
        },
        color: {
          _hover: 'IconButton.outlined.neutral-fixed.icon.hover',
          _active: 'IconButton.outlined.neutral-fixed.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.bare.brand.bg.hover',
          _active: 'IconButton.bare.brand.bg.pressed',
        },
        color: {
          _hover: 'IconButton.bare.brand.icon.hover',
          _active: 'IconButton.bare.brand.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.bare.neutral.bg.hover',
          _active: 'IconButton.bare.neutral.bg.pressed',
        },
        color: {
          _hover: 'IconButton.bare.neutral.icon.hover',
          _active: 'IconButton.bare.neutral.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.bare.critical.bg.hover',
          _active: 'IconButton.bare.critical.bg.pressed',
        },
        color: {
          _hover: 'IconButton.bare.critical.icon.hover',
          _active: 'IconButton.bare.critical.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.bare.neutral-fixed.bg.hover',
          _active: 'IconButton.bare.neutral-fixed.bg.pressed',
        },
        color: {
          _hover: 'IconButton.bare.neutral-fixed.icon.hover',
          _active: 'IconButton.bare.neutral-fixed.icon.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.filled.brand.bg.hover',
          _active: 'IconButton.onImpact.filled.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.filled.brand.border.hover',
          _active: 'IconButton.onImpact.filled.brand.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.filled.brand.icon.hover',
          _active: 'IconButton.onImpact.filled.brand.icon.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.filled.critical.bg.hover',
          _active: 'IconButton.onImpact.filled.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.filled.critical.border.hover',
          _active: 'IconButton.onImpact.filled.critical.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.filled.critical.icon.hover',
          _active: 'IconButton.onImpact.filled.critical.icon.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'neutral-fixed',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.filled.neutral-fixed.bg.hover',
          _active: 'IconButton.onImpact.filled.neutral-fixed.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.filled.neutral-fixed.border.hover',
          _active: 'IconButton.onImpact.filled.neutral-fixed.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.filled.neutral-fixed.icon.hover',
          _active: 'IconButton.onImpact.filled.neutral-fixed.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.outlined.brand.bg.hover',
          _active: 'IconButton.onImpact.outlined.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.outlined.brand.border.hover',
          _active: 'IconButton.onImpact.outlined.brand.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.outlined.brand.icon.hover',
          _active: 'IconButton.onImpact.outlined.brand.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.outlined.neutral.bg.hover',
          _active: 'IconButton.onImpact.outlined.neutral.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.outlined.neutral.border.hover',
          _active: 'IconButton.onImpact.outlined.neutral.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.outlined.neutral.icon.hover',
          _active: 'IconButton.onImpact.outlined.neutral.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.outlined.critical.bg.hover',
          _active: 'IconButton.onImpact.outlined.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.outlined.critical.border.hover',
          _active: 'IconButton.onImpact.outlined.critical.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.outlined.critical.icon.hover',
          _active: 'IconButton.onImpact.outlined.critical.icon.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.outlined.neutral-fixed.bg.hover',
          _active: 'IconButton.onImpact.outlined.neutral-fixed.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'IconButton.onImpact.outlined.neutral-fixed.border.hover',
          _active: 'IconButton.onImpact.outlined.neutral-fixed.border.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.outlined.neutral-fixed.icon.hover',
          _active: 'IconButton.onImpact.outlined.neutral-fixed.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.bare.neutral.bg.hover',
          _active: 'IconButton.onImpact.bare.neutral.bg.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.bare.neutral.icon.hover',
          _active: 'IconButton.onImpact.bare.neutral.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.bare.neutral-fixed.bg.hover',
          _active: 'IconButton.onImpact.bare.neutral-fixed.bg.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.bare.neutral-fixed.icon.hover',
          _active: 'IconButton.onImpact.bare.neutral-fixed.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.bare.brand.bg.hover',
          _active: 'IconButton.onImpact.bare.brand.bg.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.bare.brand.icon.hover',
          _active: 'IconButton.onImpact.bare.brand.icon.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      onImpact: true,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'IconButton.onImpact.bare.critical.bg.hover',
          _active: 'IconButton.onImpact.bare.critical.bg.pressed',
        },
        color: {
          _hover: 'IconButton.onImpact.bare.critical.icon.hover',
          _active: 'IconButton.onImpact.bare.critical.icon.pressed',
        },
      },
    },
  ],
});
